import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSendTransfer } from './useSendTransfer';
import { issueQuoteApi, issueCardApi } from '../lib/cards';
import { ApiError, rpcErrorCode } from '../lib/api';
import { TransferSettlementError, waitForTransferSettlement } from '../lib/transfers';

export type IssueStage = 'idle' | 'quoting' | 'paying' | 'confirming' | 'issuing';
export type IssueResult = Awaited<ReturnType<typeof issueCardApi>>;

function randomKey(): string {
  const g = globalThis.crypto;
  if (g?.randomUUID) return g.randomUUID();
  return `k${Date.now()}${Math.random().toString(16).slice(2)}`;
}

/** Fresh apply key per attempt — paymentTransferId is the durable identity.
 *  Kept ≤64 chars for the API schema. */
function issueIdempotencyKey(transferId: string): string {
  return `c-${transferId}-${randomKey().replace(/-/g, '').slice(0, 8)}`;
}

/**
 * Issuing a card: price it, pay for it, wait for on-chain confirm, then order it.
 *
 * After broadcast the transfer id is retained so retries never re-pay. Failed
 * provider applies reclaim the same payment server-side (Incpt) with a fresh
 * issue idempotency key and the original Banana external id.
 */
export function useIssueCard() {
  const qc = useQueryClient();
  const { send } = useSendTransfer();
  const [stage, setStage] = useState<IssueStage>('idle');
  const paidRef = useRef<{
    templateId: number;
    transferId: string;
  } | null>(null);
  const activeRef = useRef<Promise<IssueResult> | null>(null);

  const rememberPaid = useCallback((templateId: number, transferId: string) => {
    paidRef.current = { templateId, transferId };
  }, []);

  const clearPaid = useCallback(() => {
    paidRef.current = null;
  }, []);

  function issue(params: {
    templateId: number;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
    email?: string;
  }): Promise<IssueResult> {
    // A synchronous ref closes the double-tap window before React can re-render.
    if (activeRef.current) return activeRef.current;
    const task = runIssue(params).finally(() => {
      activeRef.current = null;
    });
    activeRef.current = task;
    return task;
  }

  async function runIssue(params: {
    templateId: number;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
    email?: string;
  }): Promise<IssueResult> {
    const { templateId, walletId, index, address, reauthToken, email } = params;
    try {
      let paid = paidRef.current;
      if (paid && paid.templateId !== templateId) {
        throw new ApiError(409, 'issue_payment_pending');
      }

      if (!paid) {
        setStage('quoting');
        const quote = await issueQuoteApi(templateId);

        // Against the provider's test environment the card isn't real, so the
        // wallet — which holds actual money — is left alone.
        if (quote.resumeTransferId) {
          paid = { templateId, transferId: quote.resumeTransferId };
          paidRef.current = paid;
        } else if (!quote.paymentRequired) {
          paid = null;
        } else {
          setStage('paying');
          const { transferId } = await send({
            walletId,
            index,
            address,
            to: quote.payTo,
            amount: String(quote.priceUsd),
            coin: 'USDT',
            reauthToken,
            cardPayment: { kind: 'issue', templateId },
          });
          if (!transferId) throw new ApiError(502, 'payment_failed');

          // From this point onward money may be on-chain. Keep the transfer
          // before waiting so every retry resumes instead of paying.
          paid = { templateId, transferId };
          paidRef.current = paid;
        }
      }

      if (paid) {
        setStage('confirming');
        try {
          await waitForTransferSettlement(paid.transferId);
        } catch (error) {
          if (error instanceof TransferSettlementError) paidRef.current = null;
          throw error;
        }
      }

      setStage('issuing');
      const request = {
        templateId,
        ...(paid ? { paymentTransferId: paid.transferId } : {}),
        // Fresh key each apply so a failed intent can be reclaimed; the payment
        // id is what prevents a second charge.
        idempotencyKey: paid ? issueIdempotencyKey(paid.transferId) : randomKey(),
        reauthToken,
        ...(email ? { email } : {}),
      };

      // A response can be lost after the server accepted the intent. Retry the
      // same request body once for transport blips; permanent failures keep
      // paidRef so the user can retry apply without re-paying.
      let res: Awaited<ReturnType<typeof issueCardApi>> | null = null;
      const lastKey = request.idempotencyKey;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          res = await issueCardApi({ ...request, idempotencyKey: lastKey });
          break;
        } catch (error) {
          const code = rpcErrorCode(error);
          // Stale resume after treasury env change — drop payment and let the
          // next attempt quote a fresh payTo / new transfer.
          if (code === 'payment_invalid' || code === 'payment_too_small' || code === 'payment_not_found') {
            paidRef.current = null;
            throw error;
          }
          const transient = ['timeout', 'ws_timeout', 'ws_closed'].includes(code);
          if (!transient || attempt === 2) throw error;
          // Keep the same key for transport retries so we replay the same intent.
          await new Promise((resolve) => setTimeout(resolve, 1_000));
        }
      }
      if (!res) throw new ApiError(502, 'issue_failed');

      // Only drop the payment once a card row exists. A pending/sent intent may
      // still fail asynchronously — Retry reclaims the same transfer.
      if (res.card) paidRef.current = null;

      void qc.invalidateQueries({ queryKey: ['cards'] });
      void qc.invalidateQueries({ queryKey: ['cardIssueStatus'] });
      setStage('idle');
      return res;
    } catch (e) {
      setStage('idle');
      throw e;
    }
  }

  return {
    stage,
    hasPendingPayment: () => paidRef.current !== null,
    rememberPaid,
    clearPaid,
    issue,
  };
}
